import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { ISignageItem, SignageOrientation } from '../../types/signage';
import { IPreviewWindow, previewWindow } from './previewLayout';
import { useMediaSrc } from './MediaThumb';
import { advance, IPlaybackPosition, loopsRequired, timedLoopSeconds, visibleItems } from './playback';

interface KioskPreviewProps {
  items: ISignageItem[];
  selectedId: string | null;
  playing: boolean;
  /** How the store's device is held. */
  orientation?: SignageOrientation;
  /** A scale being edited for an item (not saved yet); it wins over the item's saved scale. */
  scaleOverride?: { itemId: string; scale: number };
}

const Slide: React.FC<{ item: ISignageItem; layout: IPreviewWindow; onLoopEnd: () => void }> = ({ item, layout, onLoopEnd }) => {
  const src = useMediaSrc(item.fileId, item.sourceUrl);
  // The media is scaled to the window's height (portrait) or width (landscape), centred, then enlarged by the zoom.
  const fitStyle: React.CSSProperties =
    layout.fit === 'height'
      ? { height: '100%', width: 'auto', maxWidth: 'none' }
      : { width: '100%', height: 'auto', maxHeight: 'none' };
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: '#101418',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        '& img, & video': { ...fitStyle, flex: 'none', display: 'block', transform: `scale(${layout.zoom})`, transformOrigin: 'center' },
      }}
    >
      {src &&
        (item.mediaType === 'video' ? (
          <video src={src} autoPlay muted playsInline onEnded={onLoopEnd} onError={onLoopEnd} />
        ) : (
          <img src={src} alt="" />
        ))}
    </Box>
  );
};

const Placeholder: React.FC<{ label: string; sx?: object }> = ({ label, sx }) => (
  <Box sx={{ position: 'absolute', bgcolor: '#161A20', border: '1px solid #262C35', borderRadius: '8%/5%', display: 'grid', placeItems: 'center', color: '#6C7683', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', ...sx }}>
    {label}
  </Box>
);

// Plays a store's playlist with the same rules as the kiosk: images by seconds,
// GIFs and videos by loop count, one visible item repeats forever.
const KioskPreview: React.FC<KioskPreviewProps> = ({ items, selectedId, playing, orientation, scaleOverride }) => {
  const landscape = orientation === 'landscape';
  const visible = useMemo(() => visibleItems(items), [items]);
  const visibleKey = visible.map((item) => `${item.id}:${item.loopCount}:${item.durationSeconds}:${item.mediaType}`).join('|');
  const [position, setPosition] = useState<IPlaybackPosition>({ index: 0, loop: 1 });

  // Restart from the selected item (or the top) when the playlist or selection changes.
  useEffect(() => {
    const start = Math.max(0, visible.findIndex((item) => item.id === selectedId));
    setPosition({ index: start, loop: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, selectedId]);

  const pinned = !playing ? items.find((item) => item.id === selectedId) : undefined;
  const current = pinned ?? visible[position.index % Math.max(1, visible.length)];

  // Each media has its own scale (an unsaved edit counts), as on the kiosk.
  const layout = previewWindow(orientation, scaleOverride && current && scaleOverride.itemId === current.id ? scaleOverride.scale : current?.scalePercent);

  const finishLoop = React.useCallback(() => setPosition((previous) => advance(visible, previous)), [visible]);

  // Images and GIFs are driven by a timer; videos by their `ended` event.
  useEffect(() => {
    if (!playing || !current || pinned || current.mediaType === 'video') return;
    const timer = setTimeout(finishLoop, timedLoopSeconds(current) * 1000);
    return () => clearTimeout(timer);
  }, [playing, current, pinned, position, finishLoop]);

  // The device: header, the scanner and the area the media window sits in, as in the kiosk app.
  const frame = (content: React.ReactNode) => (
    <Box
      sx={{
        position: 'relative',
        mx: 'auto',
        width: '100%',
        maxWidth: landscape ? 'none' : 260,
        aspectRatio: landscape ? '20 / 9' : '9 / 20',
        bgcolor: '#0E1115',
        border: '5px solid #2a2f37',
        borderRadius: 4,
        overflow: 'hidden',
        color: '#fff',
      }}
    >
      <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: landscape ? '14%' : '6.5%', bgcolor: '#161A20', borderBottom: '1px solid #262C35', display: 'flex', alignItems: 'center', px: '3%', gap: 1 }}>
        <Box sx={{ width: landscape ? '1.6%' : '8%', aspectRatio: '1', bgcolor: '#E04B45', borderRadius: '25%' }} />
        {landscape && <Typography sx={{ mx: 'auto', fontSize: 11, fontWeight: 800 }}>14:20 Fri 16 Oct</Typography>}
      </Box>
      {landscape ? (
        <>
          <Placeholder label="Camera" sx={{ right: '2%', top: '17%', width: '25%', height: '40%' }} />
          <Placeholder label="Ready to scan" sx={{ right: '2%', top: '59%', width: '25%', height: '38%' }} />
        </>
      ) : (
        <>
          <Placeholder label="Camera" sx={{ left: '3.5%', top: '8.5%', width: '46%', height: '19%' }} />
          <Placeholder label="Ready to scan" sx={{ right: '3.5%', top: '8.5%', width: '46%', height: '19%' }} />
          <Typography sx={{ position: 'absolute', left: '4%', bottom: '1.5%', fontSize: 10, fontWeight: 800 }}>14:20</Typography>
        </>
      )}
      {/* Media area; the window inside it follows the scale */}
      <Box
        data-testid="kiosk-preview-area"
        sx={
          landscape
            ? { position: 'absolute', left: '2%', top: '17%', bottom: '3%', width: '70%' }
            : { position: 'absolute', left: '3.5%', right: '3.5%', top: '30%', bottom: '6%' }
        }
      >
        {content}
      </Box>
    </Box>
  );

  if (!current) {
    return frame(
      <Box sx={{ position: 'absolute', inset: 0, borderRadius: 2, bgcolor: 'rgba(255,255,255,.06)', display: 'grid', placeItems: 'center', p: 2 }}>
        <Typography sx={{ color: '#A7B0BC' }} variant="body2" textAlign="center">
          No media to preview
        </Typography>
      </Box>
    );
  }

  const index = items.indexOf(current) + 1;
  const required = visible.length === 1 ? '∞' : String(loopsRequired(current));

  return frame(
    <Box
      data-testid="kiosk-preview-window"
      sx={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: `${layout.widthPercent}%`,
        height: `${layout.heightPercent}%`,
        transform: 'translate(-50%, -50%)',
        borderRadius: 2,
        overflow: 'hidden',
        containerType: 'inline-size',
      }}
    >
      <Slide key={`${current.id}-${current.mediaType === 'image' ? 0 : position.loop}-${pinned ? 'p' : 'a'}`} item={current} layout={layout} onLoopEnd={finishLoop} />
      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,.35) 0%, transparent 35%, rgba(0,0,0,.65) 100%)' }} />
      <Chip
        size="small"
        label={`${current.mediaType.toUpperCase()} · ${index}/${items.length}${current.mediaType === 'image' ? '' : ` · loop ${position.loop}/${required}`}`}
        sx={{ position: 'absolute', left: '4%', top: '6%', bgcolor: 'rgba(0,0,0,.55)', color: '#fff', fontWeight: 700, maxWidth: '90%' }}
      />
      {current.tag && (
        <Chip
          size="small"
          label={current.tag}
          sx={{ position: 'absolute', right: '4%', top: '6%', bgcolor: 'rgba(255,255,255,.18)', color: '#fff', fontWeight: 700, border: '1px solid rgba(255,255,255,.3)' }}
        />
      )}
      <Box sx={{ position: 'absolute', left: '5%', right: '30%', bottom: '8%' }}>
        {current.eyebrow && (
          <Typography sx={{ fontSize: 'clamp(9px, 1.7cqw, 13px)', letterSpacing: '.16em', textTransform: 'uppercase', fontWeight: 700, opacity: 0.9 }}>
            {current.eyebrow}
          </Typography>
        )}
        <Typography sx={{ fontSize: 'clamp(14px, 5cqw, 40px)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-.02em', mt: 0.5 }}>{current.title}</Typography>
        {current.subtitle && <Typography sx={{ fontSize: 'clamp(10px, 2cqw, 16px)', opacity: 0.92, mt: 1 }}>{current.subtitle}</Typography>}
      </Box>
      {!current.enabled && (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,.6)', display: 'grid', placeItems: 'center', textAlign: 'center', p: 2 }}>
          <Typography sx={{ fontWeight: 800, letterSpacing: '.12em', fontSize: 11 }}>HIDDEN · NOT SHOWN ON THE KIOSK</Typography>
        </Box>
      )}
    </Box>
  );
};

export default KioskPreview;
